import { useState } from 'react';
import { GlassButton } from '@/components/GlassButton';
import { Reveal } from '@/components/Reveal';
import { SectionHeading } from '@/components/SectionHeading';
import { SpotlightCard } from '@/components/SpotlightCard';

type Cycle = 'monthly' | 'quarterly' | 'yearly';

const CYCLES: Record<Cycle, { price: string; cadence: string; perMonth: string; save?: string }> = {
  monthly:   { price: '¥29',  cadence: '/ 月', perMonth: '¥29.00 / 月' },
  quarterly: { price: '¥78',  cadence: '/ 季', perMonth: '¥26.00 / 月', save: '9 折' },
  yearly:    { price: '¥278', cadence: '/ 年', perMonth: '¥23.17 / 月', save: '8 折 · 省 ¥70' },
};

export function PricingPage(): JSX.Element {
  const [cycle, setCycle] = useState<Cycle>('yearly');
  const vipPrice = CYCLES[cycle];

  return (
    <div className="relative">
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 -z-10 bg-grid opacity-40" />
        <div className="absolute inset-0 -z-10 bg-radial-warm" />

        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Pricing · 会员计划"
              title={<>核心玩法 <span className="text-gradient-warm">永远免费</span></>}
              subtitle="只在你想解锁全库 1000+ 课程时再考虑升级 VIP — 价格永远低于一杯咖啡。"
            />
          </Reveal>

          {/* Cycle toggle */}
          <Reveal delay={0.05}>
            <div className="mt-10 flex justify-center">
              <div className="inline-flex gap-1 rounded-full bg-ink-800/60 border border-honey-400/20 p-1">
                {(['monthly', 'quarterly', 'yearly'] as Cycle[]).map((k) => (
                  <button
                    key={k}
                    onClick={() => setCycle(k)}
                    className={`px-4 py-2 rounded-full text-xs font-arcade tracking-widest transition-all ${
                      cycle === k
                        ? 'bg-honey-400/20 text-honey-200 glow-cyan'
                        : 'text-cream-400 hover:text-white'
                    }`}
                  >
                    {k === 'monthly' ? '月付' : k === 'quarterly' ? '季付 9折' : '年付 8折'}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <Reveal delay={0.08}>
              <SpotlightCard spotlightColor="rgba(216, 216, 232, 0.18)" className="h-full">
                <div className="text-sm font-semibold text-cream-300">Free</div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="font-display text-5xl font-extrabold text-white">¥0</span>
                  <span className="text-sm text-cream-400">永久</span>
                </div>
                <p className="mt-3 text-sm text-cream-300/90">核心玩法永久免费 · 限 10 门课 + 2 个单词本</p>
                <ul className="mt-6 space-y-3 text-sm text-cream-200">
                  {[
                    '拼图 / 打字 / 口语 三大玩法',
                    '无限畅玩 · 没有"体力值"',
                    'AI 智能纠错（基础版）',
                    'AI 口语测评 30 次 / 日',
                    '错题本 + 艾宾浩斯复习',
                  ].map((f) => (
                    <li key={f} className="flex gap-2.5">
                      <span className="text-mint-400">✓</span><span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <GlassButton to="/app" size="md" className="w-full">立即开始</GlassButton>
                </div>
              </SpotlightCard>
            </Reveal>

            <Reveal delay={0.14}>
              <SpotlightCard
                spotlightColor="rgba(0, 240, 255, 0.32)"
                className="h-full border-honey-400/40 bg-honey-400/[0.04]"
              >
                <div className="absolute -top-3 right-6 rounded-full bg-gradient-to-r from-honey-400 to-ember-500 px-3 py-1 text-xs font-arcade text-ink-950">
                  最热门
                </div>
                <div className="text-sm font-semibold text-honey-300">VIP</div>
                <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                  <span className="font-display text-5xl font-extrabold text-white">{vipPrice.price}</span>
                  <span className="text-sm text-cream-400">{vipPrice.cadence}</span>
                  {vipPrice.save !== undefined && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-ember-500/20 text-ember-300 border border-ember-400/40">
                      {vipPrice.save}
                    </span>
                  )}
                </div>
                <div className="text-xs text-cream-400 mt-1 font-mono">{vipPrice.perMonth}</div>
                <p className="mt-3 text-sm text-cream-300/90">解锁全库 1000+ 课程 + AI Pro 引擎</p>
                <ul className="mt-6 space-y-3 text-sm text-cream-200">
                  {[
                    '全库 1000+ 套课程无限解锁',
                    'AI 智能纠错 · Pro 引擎',
                    'AI 口语测评 300 次 / 日',
                    '无限单词本 + K12 教材同步',
                    '推广分佣 · 拉新得佣金',
                  ].map((f) => (
                    <li key={f} className="flex gap-2.5">
                      <span className="text-honey-300">✓</span><span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <GlassButton to="/login" size="md" variant="solid" className="w-full">
                    开通 VIP · {vipPrice.price}
                  </GlassButton>
                </div>
              </SpotlightCard>
            </Reveal>
          </div>

          <Reveal>
            <div className="mt-16 rounded-3xl border border-white/[0.06] bg-white/[0.02] p-8 md:p-10">
              <div className="font-display text-2xl font-bold text-white mb-4">常见问题</div>
              <div className="grid gap-6 md:grid-cols-2 text-sm">
                {[
                  ['免费版能用多久？', '永久免费。三大玩法永远免费，限 10 门课程 + 2 个单词本，AI 口语测评 30 次/日。'],
                  ['VIP 退款？', '7 天无理由退款 — 开通后 7 天内不满意，联系客服全额退款。'],
                  ['支付方式？', '微信支付 / 支付宝 两种都支持。'],
                  ['推广能赚多少？', '每个朋友升级 VIP 你拿 ¥6 ~ ¥30。VIP 年卡转化分你 ¥30。'],
                ].map(([q, a]) => (
                  <div key={q}>
                    <div className="text-white font-semibold">{q}</div>
                    <div className="mt-1.5 text-cream-400 leading-relaxed">{a}</div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
